import React, { FC, useState } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  Box,
  Link,
  Pill,
  ScreenReaderOnly,
  Text,
  VerticalStack,
} from '@moonpig/launchpad-components'
import { Selector, SelectorContent } from '@moonpig/launchpad-forms'
import { ProductVariantPriceDisplay } from '@moonpig/web-shared-memberships'
import { ProductVariantPrice } from '@moonpig/web-shared-types-graphql'
import { getVariantIcon } from '../../utils/getVariantIcon'
import { SelectAmountWarning } from '../VariantSelectorPrice/SelectAmountWarning'
import { useBasket } from '../../contexts/basket'
import { useProductsLocaleText } from '../../locale'
import {
  LARGE_CARD_SIZE_ID,
  LARGE_FLOWER_SIZE_ID,
  LARGE_SQUARE_CARD_SIZE_ID,
  E_CARD_SIZE_ID,
} from '../../constants'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useStoreId } from '@moonpig/web-core-stores'

const StyledFieldset = styled.fieldset`
  padding: 0;
  margin: 0;
  border-width: 0px;
  text-align: left;
`
const StyledFooterLink = styled(Link)`
  ${s({ color: 'colorTextLabel' })}
  text-decoration: underline;
`

const StyledPillContainer = styled(Box)`
  ${s({ paddingY: 3 })};
`

export type VariantSelectorItem = {
  label: string
  description?: string
  value: string
  price: string
  discountedPrice?: string
  absolutePrice?: string
  memberPrice?: ProductVariantPrice
  dimensions?:
    | {
        description: string
      }
    | string
    | null
}

export type VariantSelectorProps = {
  title?: string
  selectedValue: string
  items: VariantSelectorItem[]
  isLandscape: boolean
  onChange: (variantValue: string) => void
  titleIsScreenReaderOnly?: boolean
}

const StyledPricePlusDelivery = styled(Text)`
  ${s({ ml: 3 })};
`

const VariantSelectorPrice = ({
  price,
  discountedPrice,
  label,
}: {
  label: string
  price: string
  discountedPrice?: string
}) => {
  return discountedPrice ? (
    <>
      <Text
        data-testid={`${label}-discounted-price`}
        typography="typeBodyLabel"
        aria-hidden="true"
      >
        {discountedPrice}
      </Text>{' '}
      <Text
        data-testid={`${label}-price`}
        color="colorBlack80"
        aria-hidden="true"
      >
        <s>{price}</s>
      </Text>
    </>
  ) : (
    <span data-testid={`${label}-price`} aria-hidden="true">
      {price}
    </span>
  )
}

export const VariantSelector: FC<VariantSelectorProps> = ({
  title,
  selectedValue,
  items,
  isLandscape,
  onChange,
  titleIsScreenReaderOnly = false,
}) => {
  const ConditionalScreenReaderWrapper = titleIsScreenReaderOnly
    ? ScreenReaderOnly
    : React.Fragment

  const [state, { setSelectedAmountWarning }] = useBasket()
  const { selectedAmountWarning } = state
  const [slideOut, setSlideOut] = useState(false)
  const selectorRef = React.useRef<HTMLInputElement>(null)
  const region = useStoreId()

  const localiseText = useProductsLocaleText()
  const isECardExclusive =
    items.findIndex(item => item.value !== E_CARD_SIZE_ID) === -1

  const isDripPricingEnabled =
    useExperiment('drip-pricing', {
      fallback: 'control',
    }) === 'variant'

  const dripPricingEnabled = isDripPricingEnabled && !isECardExclusive

  const inLargeFlowerPillExperiment =
    useExperiment('attach-large-flowers-variant-pill', {
      fallback: 'control',
    }) === 'variant'

  const variantsForPills = [
    LARGE_CARD_SIZE_ID,
    LARGE_SQUARE_CARD_SIZE_ID,
    inLargeFlowerPillExperiment ? LARGE_FLOWER_SIZE_ID : null,
  ]

  return (
    <StyledFieldset data-testid="variant-selector">
      <ConditionalScreenReaderWrapper>
        <Text
          data-testid="variant-select-title"
          as="legend"
          typography={{ xs: 'typeMobileDisplay03', md: 'typeDesktopDisplay05' }}
          color={'colorTextHeadline'}
          p="0"
          m="0"
          mb={4}
        >
          {title}
        </Text>
      </ConditionalScreenReaderWrapper>
      <VerticalStack spacing={3} mb={4}>
        {items.map(
          ({
            label,
            description,
            price,
            value,
            discountedPrice,
            absolutePrice,
            dimensions,
            memberPrice,
          }) => {
            const screenReaderPrice = discountedPrice || absolutePrice || price

            const dimensionsLabel =
              typeof dimensions === 'string'
                ? dimensions
                : dimensions?.description

            const dimensionsScreenReaderLabel = dimensions
              ? `Dimensions: ${dimensionsLabel}`
              : null

            const screenReaderLabel = [
              label,
              screenReaderPrice,
              description,
              dimensionsScreenReaderLabel,
            ]
              .filter(labelPart => !!labelPart)
              .join(' - ')

            const showPill = description && variantsForPills.includes(value)

            const isECard = value === E_CARD_SIZE_ID

            const pricePlusDelivery =
              dripPricingEnabled && !isECard ? (
                <StyledPricePlusDelivery
                  typography="typeBodyText"
                  color="colorTextBody"
                >
                  {localiseText('variant_selector.plus_delivery')}
                </StyledPricePlusDelivery>
              ) : null

            return (
              <Selector
                key={label}
                type="radio"
                name="variant-selector"
                aria-describedby="validation-error"
                id={`variant-selector-${value}`}
                value={value}
                checked={selectedValue === value}
                onChange={() => {
                  onChange(value)
                }}
                ref={selectorRef}
              >
                <SelectorContent
                  title={
                    <>
                      <div
                        data-testid={`${label}-selector-heading`}
                        aria-hidden="true"
                      >
                        {label}
                      </div>
                      <ScreenReaderOnly
                        testId={`${label}-selector-screenreader-text`}
                      >
                        {screenReaderLabel}
                      </ScreenReaderOnly>
                    </>
                  }
                  body={
                    <div
                      data-testid={`${label}-description`}
                      aria-hidden="true"
                    >
                      {showPill ? (
                        <StyledPillContainer>
                          <Pill
                            label={description}
                            data-testid="variant-description-pill"
                          />
                        </StyledPillContainer>
                      ) : (
                        description || ''
                      )}
                    </div>
                  }
                  label={
                    <ProductVariantPriceDisplay
                      fallback={
                        <VariantSelectorPrice
                          label={label}
                          price={price}
                          discountedPrice={discountedPrice}
                        />
                      }
                      price={memberPrice}
                      plusDelivery={pricePlusDelivery}
                    />
                  }
                  icon={getVariantIcon({ sizeId: value, isLandscape })}
                />
              </Selector>
            )
          },
        )}

        <SelectAmountWarning
          slideOut={slideOut}
          selectedAmountWarning={selectedAmountWarning}
          setSlideOut={setSlideOut}
          setSelectedAmountWarning={setSelectedAmountWarning}
          triggerRef={selectorRef}
          copyOverrides={{
            selectAmountToContinue: localiseText(
              'price_variant_selector.select_bouquet_size_to_continue',
            ),
            closeSelectAmountWarning: localiseText(
              'price_variant_selector.close_bouquet_size_warning',
            ),
          }}
        />
      </VerticalStack>
      {dripPricingEnabled && (
        <ConditionalScreenReaderWrapper>
          <Text typography="typeBodyCaption" color={'colorTextLabel'}>
            {localiseText('variant_selector.footer_line_one')}
            <br />
            {localiseText('variant_selector.footer_line_two_part_one')}
            <StyledFooterLink
              href={`/${region}/delivery-information`}
              target="_blank"
            >
              {localiseText('variant_selector.footer_line_two_part_two')}
            </StyledFooterLink>
            {localiseText('variant_selector.footer_line_two_part_three')}
          </Text>
        </ConditionalScreenReaderWrapper>
      )}
    </StyledFieldset>
  )
}
